import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle2, XCircle, AlertCircle, Shield, Wifi, Users, Tag } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';

interface HealthStatus {
  status: 'healthy' | 'unhealthy' | 'unknown' | 'checking';
  message?: string;
  timestamp?: string;
  authenticated?: boolean;
  user?: {
    username: string;
    email: string;
  };
}

interface VersionInfo {
  version: string;
  buildTime: string;
  gitCommit: string;
  gitBranch: string;
  buildNumber: string;
  environment: string;
}

interface BackendVersionInfo {
  version: string;
  buildTime: string;
  gitCommit: string;
  buildNumber: string;
  environment: string;
}

export function HealthCheck() {
  const [authHealth, setAuthHealth] = useState<HealthStatus>({
    status: 'checking',
    message: 'Checking Auth...'
  });
  const [wsHealth, setWsHealth] = useState<HealthStatus>({
    status: 'checking',
    message: 'Checking WebSocket...'
  });
  const [showDetails, setShowDetails] = useState(false);
  const [cognitoGroups, setCognitoGroups] = useState<string[]>([]);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [backendVersionInfo, setBackendVersionInfo] = useState<BackendVersionInfo | null>(null);
  const [shouldShow, setShouldShow] = useState(false);

  // Check if component should be visible (dev: all users, prod: super_admin only)
  useEffect(() => {
    const checkVisibility = async () => {
      const env = import.meta.env.VITE_ENVIRONMENT || 'dev';

      // Always show in dev
      if (env === 'dev') {
        setShouldShow(true);
        return;
      }

      // In prod, only show to super_admin
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const groups = (accessToken?.payload['cognito:groups'] as string[]) || [];
        setShouldShow(groups.includes('super_admin'));
      } catch {
        setShouldShow(false);
      }
    };

    checkVisibility();
  }, []);

  // Fetch version info
  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const response = await fetch('/version.json');
        if (response.ok) {
          const data = await response.json();
          setVersionInfo(data);
        }
      } catch (error) {
        console.error('Error loading version info:', error);
      }
    };

    loadVersionInfo();
  }, []);

  // Fetch Cognito groups using Amplify v6 API
  useEffect(() => {
    const loadCognitoGroups = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const idToken = session.tokens?.idToken;

        // Get groups from access token or ID token
        const accessGroups = (accessToken?.payload['cognito:groups'] as string[]) || [];
        const idGroups = (idToken?.payload['cognito:groups'] as string[]) || [];

        const groups = accessGroups.length > 0 ? accessGroups : idGroups;
        setCognitoGroups(groups);
      } catch (error) {
        console.error('Error fetching Cognito groups:', error);
        setCognitoGroups([]);
      }
    };

    loadCognitoGroups();
  }, []);

  const checkHealth = async () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://e11vh8tduc.execute-api.us-east-1.amazonaws.com/v1';

    // Check authenticated API health (ALL endpoints require auth now)
    try {
      setAuthHealth({ status: 'checking', message: 'Checking Auth...' });

      // Get Cognito ID token (required by API Gateway Cognito Authorizer)
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setAuthHealth({
          status: 'unhealthy',
          message: 'No auth token',
          authenticated: false
        });
        return;
      }

      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Extract backend version info
        if (data.backend) {
          setBackendVersionInfo(data.backend);
        }

        setAuthHealth({
          status: 'healthy',
          message: data.authenticated ? 'Authenticated' : 'Not authenticated',
          timestamp: data.timestamp,
          authenticated: data.authenticated,
          user: data.user
        });
      } else if (response.status === 401) {
        setAuthHealth({
          status: 'unhealthy',
          message: 'Token invalid',
          authenticated: false
        });
      } else {
        setAuthHealth({
          status: 'unhealthy',
          message: `Status ${response.status}`,
          authenticated: false
        });
      }
    } catch (error) {
      console.error('Auth health check failed:', error);
      setAuthHealth({
        status: 'unhealthy',
        message: 'Auth check failed',
        authenticated: false
      });
    }

    // Check WebSocket connectivity
    try {
      setWsHealth({ status: 'checking', message: 'Checking WebSocket...' });

      const wsUrl = import.meta.env.VITE_WEBSOCKET_URL || 'wss://slo8hiuq84.execute-api.us-east-1.amazonaws.com/production';

      // Get Cognito ACCESS token for WebSocket authentication
      // Backend uses cognito-idp:get_user which requires ACCESS token (not ID token)
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        setWsHealth({
          status: 'unhealthy',
          message: 'No auth token for WebSocket'
        });
        return;
      }

      // WebSocket URL with token query parameter (required by backend)
      const wsUrlWithAuth = `${wsUrl}?token=${accessToken}`;

      // Test WebSocket connection with timeout
      await new Promise<void>((resolve, reject) => {
        const ws = new WebSocket(wsUrlWithAuth);
        const timeout = setTimeout(() => {
          ws.close();
          reject(new Error('Connection timeout'));
        }, 5000);

        ws.onopen = () => {
          clearTimeout(timeout);
          setWsHealth({
            status: 'healthy',
            message: 'Connected',
            timestamp: new Date().toISOString()
          });
          ws.close();
          resolve();
        };

        ws.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };

        ws.onclose = (event) => {
          if (!event.wasClean) {
            clearTimeout(timeout);
            reject(new Error('Connection failed'));
          }
        };
      });
    } catch (error) {
      console.error('WebSocket health check failed:', error);
      setWsHealth({
        status: 'unhealthy',
        message: 'Connection failed'
      });
    }
  };

  // Check health on mount and every 30 seconds
  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const getOverallStatus = () => {
    if (authHealth.status === 'checking' || wsHealth.status === 'checking') {
      return 'checking';
    }
    if (authHealth.status === 'healthy' && wsHealth.status === 'healthy') {
      return 'healthy';
    }
    return 'unhealthy';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'unhealthy':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'checking':
        return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'unhealthy':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'checking':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const overallStatus = getOverallStatus();

  // Hide in prod unless super_admin
  if (!shouldShow) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${getStatusColor(overallStatus)} transition-all hover:shadow-sm`}
        title="Click for details"
      >
        {getStatusIcon(overallStatus)}
        <span className="text-xs font-medium">Status</span>
      </button>

      {/* Dropdown Details */}
      {showDetails && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDetails(false)}
          />

          {/* Details Panel */}
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-20 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-900">System Health Status</h3>
                <button
                  onClick={checkHealth}
                  className="text-xs text-tdb-accent hover:text-tdb-accent/80 font-medium"
                >
                  Refresh
                </button>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(overallStatus)}
                <span className={`text-sm font-semibold ${
                  overallStatus === 'healthy' ? 'text-green-600' :
                  overallStatus === 'unhealthy' ? 'text-red-600' :
                  overallStatus === 'checking' ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {overallStatus === 'healthy' ? 'All Systems Operational' :
                   overallStatus === 'unhealthy' ? 'Service Issues Detected' :
                   overallStatus === 'checking' ? 'Checking Status...' :
                   'Unknown Status'}
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Auth Health */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-700">Authentication</span>
                </div>
                <div className="ml-6 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(authHealth.status)}
                      <span className="text-gray-900">{authHealth.message}</span>
                    </div>
                  </div>
                  {authHealth.authenticated && authHealth.user && (
                    <div className="text-xs text-gray-500">
                      {authHealth.user.email}
                    </div>
                  )}
                </div>
              </div>

              {/* WebSocket Health */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Wifi className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-700">WebSocket</span>
                </div>
                <div className="ml-6 flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(wsHealth.status)}
                    <span className="text-gray-900">{wsHealth.message}</span>
                  </div>
                  {wsHealth.timestamp && (
                    <span className="text-gray-500">
                      {new Date(wsHealth.timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                    </span>
                  )}
                </div>
              </div>

              {/* Cognito Groups Debug Info */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-700">Cognito Groups</span>
                </div>
                <div className="ml-6 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Groups:</span>
                    <span className="text-gray-900 font-mono">
                      {cognitoGroups.length > 0 ? cognitoGroups.join(', ') : 'none'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Is Admin:</span>
                    <span className={`font-semibold ${cognitoGroups.includes('admin') ? 'text-green-600' : 'text-gray-400'}`}>
                      {cognitoGroups.includes('admin') ? '✅ YES' : '❌ NO'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Frontend Version Info */}
              {versionInfo && (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-semibold text-gray-700">Frontend</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Version:</span>
                      <span className="text-gray-900 font-mono font-semibold">
                        {versionInfo.version}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Commit:</span>
                      <span className="text-gray-900 font-mono">
                        {versionInfo.gitCommit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Deployed:</span>
                      <span className="text-gray-900">
                        {new Date(versionInfo.buildTime).toLocaleDateString()} {new Date(versionInfo.buildTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Backend Version Info */}
              {backendVersionInfo && (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-semibold text-gray-700">Backend</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Version:</span>
                      <span className="text-gray-900 font-mono font-semibold">
                        {backendVersionInfo.version}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Commit:</span>
                      <span className="text-gray-900 font-mono">
                        {backendVersionInfo.gitCommit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Deployed:</span>
                      <span className="text-gray-900">
                        {new Date(backendVersionInfo.buildTime).toLocaleDateString()} {new Date(backendVersionInfo.buildTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-gray-100">
                <div className="text-xs">
                  <span className="text-gray-500 block mb-1">API Endpoint:</span>
                  <span className="font-mono text-[10px] text-gray-900 break-all">
                    {import.meta.env.VITE_API_URL?.replace('https://', '') || 'e11vh8tduc...'}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-3">
              <button
                onClick={async () => {
                  try {
                    const session = await fetchAuthSession();
                    const idToken = session.tokens?.idToken?.toString();
                    const apiUrl = import.meta.env.VITE_API_URL || 'https://e11vh8tduc.execute-api.us-east-1.amazonaws.com/v1';

                    await fetch(`${apiUrl}/test-error`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                      }
                    });

                    alert('Error test triggered! Check Telegram topic 268 for error notification.');
                  } catch (error) {
                    alert('Error test triggered! (Request failed but that\'s expected)');
                  }
                }}
                className="w-full px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold border border-red-200 hover:border-red-300 transition-all"
              >
                🧪 Test Error Notifications
              </button>
              <p className="text-xs text-gray-500">
                Auto-refreshes every 30 seconds
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
